import { Grid, Typography, Button, Tabs, Tab, Box } from '@mui/material';
import { Header } from 'components/Header/Header';
import { useState, useEffect } from 'react';
import Editor from 'react-simple-wysiwyg';
import { EditorService } from 'Helpers/EditorService';
import { useDialog } from 'Contexts/Modals/ModalProvider';
import InformationDialog from 'Dialogs/Information/Information';
import { Info } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import { newServer } from 'constants/constants';

export const Policies = () => {
  const [content, setContent] = useState({
    terms: '',
    privacy: '',
  });
  const [editing, setEditing] = useState({
    terms: false,
    privacy: false,
  });
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const { openDialog } = useDialog();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [alertMessage, setAlertMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await EditorService.getTermsAndPrivacy();
      const data = response.data as { content: string }[];
      setContent({
        terms: data[0].content,
        privacy: data[1].content,
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setAlertMessage('Error loading content. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = async () => {
    try {
      console.log(user.email);

      const response = await fetch(`${newServer}login/login-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      if (!response.ok) {
        throw new Error('Failed to request OTP');
      }

      return true;
    } catch (error) {
      console.error('Error requesting OTP:', error);
      setAlertMessage('Error requesting OTP. Please try again.');
      setOpenSnackbar(true);
      return false;
    }
  };

  const showOTPDialog = async (type: 'terms' | 'privacy') => {
    const otpRequested = await requestOTP();

    if (otpRequested) {
      openDialog('information', InformationDialog as any, {
        title: 'Verify OTP Code',
        description:
          'Please enter the OTP code sent to your registered email to confirm the changes.',
        icon: <Info />,
        input: '',
        setInput: (value: string) => handleOTPVerification(type, value),
        inputPlaceholder: 'Enter OTP code',
        cancelText: 'Cancel',
        onConfirm: (value: string) => handleOTPVerification(type, value),
      });
    }
  };

  const handleOTPVerification = async (type: 'terms' | 'privacy', code: string) => {
    try {
      const response = await fetch(`${newServer}login/2FA`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          remember_me: false,
        }),
      });

      if (response.ok) {
        await saveContent(type);
        setAlertMessage('Changes saved successfully!');
        setOpenSnackbar(true);
      } else {
        setAlertMessage('Invalid OTP code. Please try again.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setAlertMessage('Error verifying OTP. Please try again.');
      setOpenSnackbar(true);
    }
  };

  const saveContent = async (type: 'terms' | 'privacy') => {
    try {
      await EditorService.updateTermPrivacy(type, content[type]);
      setEditing((prev) => ({
        ...prev,
        [type]: false,
      }));
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      setAlertMessage(`Error saving ${type}. Please try again.`);
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleEditorChange = (type: 'terms' | 'privacy', event: any) => {
    const newValue = event.target.value;
    setContent((prevContent) => ({
      ...prevContent,
      [type]: newValue,
    }));
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  if (loading) {
    return (
      <Grid className='bg-background'>
        <Header />
        <Typography textAlign='center'>Loading...</Typography>
      </Grid>
    );
  }

  const EditorContent = ({ type }: { type: 'terms' | 'privacy' }) => (
    <Box
      sx={{
        mt: 3,
        pb: 10,
        position: 'relative',
        height: 'calc(100vh - 200px)',
      }}
    >
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
        }}
      >
        {editing[type] ? (
          <Editor
            value={content[type]}
            onChange={(event) => handleEditorChange(type, event)}
            containerProps={{
              style: {
                height: '100%',
                border: '1px solid var(--border)',
                borderRadius: '4px',
              },
            }}
            className='rsw-editor'
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: content[type] }}
            style={{
              height: '100%',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              padding: '20px',
              overflow: 'auto',
            }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Grid className='bg-background mx-auto'>
      <Header />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          aria-label='Policy tabs'
        >
          <Tab label='Terms and Conditions' />
          <Tab label='Privacy Policy' />
        </Tabs>
      </Box>
      {currentTab === 0 && <EditorContent type='terms' />}
      {currentTab === 1 && <EditorContent type='privacy' />}

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          backgroundColor: 'background.paper',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <Button
          variant='contained'
          color={editing[currentTab === 0 ? 'terms' : 'privacy'] ? 'success' : 'primary'}
          onClick={() => {
            const type = currentTab === 0 ? 'terms' : 'privacy';
            if (editing[type]) {
              showOTPDialog(type);
            } else {
              setEditing((prev) => ({
                ...prev,
                [type]: true,
              }));
            }
          }}
        >
          {editing[currentTab === 0 ? 'terms' : 'privacy'] ? 'Save' : 'Edit'}
        </Button>
      </Box>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={alertMessage.includes('Error') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};
